class ReadMoreText extends HTMLElement {
  #controller = new AbortController();
  #resizeObserver;
  #rafHandle;
  #content = null;
  #toggle = null;
  #clampedHeight = 0;
  #isAnimating = false;

  connectedCallback() {
    this.#content = this.querySelector('[ref="content"]');
    this.#toggle = this.querySelector('[ref="toggle"]');
    const toggle = this.#toggle;
    if (!toggle) return;

    const { signal } = this.#controller;
    toggle.addEventListener('click', this.#handleToggle, { signal });

    this.#resizeObserver = new ResizeObserver(this.#updateOverflowState);
    if (this.#content) this.#resizeObserver.observe(this.#content);

    // Initial check on next frame so layout has settled.
    this.#rafHandle = requestAnimationFrame(this.#updateOverflowState);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    if (this.#rafHandle !== undefined) {
      cancelAnimationFrame(this.#rafHandle);
      this.#rafHandle = undefined;
    }
  }

  #updateOverflowState = () => {
    this.#rafHandle = undefined;
    if (!this.isConnected || this.#isAnimating) return;

    const content = this.#content;
    const toggle = this.#toggle;
    if (!content || !toggle) return;

    // When expanded, we can't measure overflow (clamp is removed) — assume it overflows.
    if (this.hasAttribute('expanded')) {
      toggle.setAttribute('data-overflows', 'true');
      return;
    }

    const next = content.scrollHeight - content.clientHeight > 1 ? 'true' : 'false';
    if (toggle.getAttribute('data-overflows') !== next) toggle.setAttribute('data-overflows', next);
  };

  #handleToggle = () => {
    const toggle = this.#toggle;
    const content = this.#content;
    if (!toggle || !content || this.#isAnimating) return;

    const expanding = !this.hasAttribute('expanded');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const isExpanded = this.toggleAttribute('expanded');
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      toggle.textContent = isExpanded ? (toggle.dataset.labelLess ?? '') : (toggle.dataset.labelMore ?? '');
      if (!isExpanded) this.#rafHandle = requestAnimationFrame(this.#updateOverflowState);
      return;
    }

    this.#isAnimating = true;

    if (expanding) {
      // Cache the clamped height before removing the clamp.
      this.#clampedHeight = content.clientHeight;
      toggle.setAttribute('aria-expanded', 'true');
      toggle.textContent = toggle.dataset.labelLess ?? '';

      // Pin at clamped height, then let CSS (via [expanded]) remove line-clamp.
      content.style.overflow = 'hidden';
      content.style.height = this.#clampedHeight + 'px';
      this.setAttribute('expanded', '');

      // Animate to full height on the next frame after layout has updated.
      requestAnimationFrame(() => {
        content.style.height = content.scrollHeight + 'px';
        content.addEventListener(
          'transitionend',
          () => {
            content.style.height = '';
            content.style.overflow = '';
            this.#isAnimating = false;
          },
          { once: true },
        );
      });
    } else {
      // Update toggle text immediately so the UI feels responsive.
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.labelMore ?? '';

      // Pin at full height. Keep [expanded] so CSS line-clamp doesn't snap mid-transition.
      content.style.overflow = 'hidden';
      content.style.height = content.scrollHeight + 'px';
      // Force reflow so the pinned height is committed; otherwise it transitions from `auto` and transitionend never fires.
      void content.offsetHeight;

      requestAnimationFrame(() => {
        content.style.height = this.#clampedHeight + 'px';
        content.addEventListener(
          'transitionend',
          () => {
            content.style.height = '';
            content.style.overflow = '';
            // Remove [expanded] only after the height has reached the clamped size,
            // so CSS line-clamp takes over without a visual jump.
            this.removeAttribute('expanded');
            this.#isAnimating = false;
            this.#rafHandle = requestAnimationFrame(this.#updateOverflowState);
          },
          { once: true },
        );
      });
    }
  };
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
